import { useState } from 'react';
import { Send, Search, Users, Filter, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { useApplicants, useSendEmail, useSendEmailAll } from '../hooks/useApi';
import { getStatusColor, getStatusLabel } from '../lib/utils';

type RecipientMode = 'select' | 'all';

export default function EmailPage() {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [recipientMode, setRecipientMode] = useState<RecipientMode>('select');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const { data: applicantsData, isLoading: loadingApplicants } = useApplicants(search);
  const sendEmail = useSendEmail();
  const sendEmailAll = useSendEmailAll();

  const applicants = applicantsData?.applicants || [];

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) {
      setFeedback({ type: 'error', message: 'Please fill in the subject and body.' });
      return;
    }

    setFeedback(null);

    try {
      if (recipientMode === 'select') {
        if (selectedIds.length === 0) {
          setFeedback({ type: 'error', message: 'Please select at least one recipient.' });
          return;
        }
        await sendEmail.mutateAsync({ subject, body, applicantIds: selectedIds });
      } else {
        await sendEmailAll.mutateAsync({
          subject,
          body,
          statusFilter: statusFilter || undefined,
        });
      }
      setFeedback({ type: 'success', message: 'Email sent successfully!' });
      setSubject('');
      setBody('');
      setSelectedIds([]);
    } catch {
      setFeedback({ type: 'error', message: 'Failed to send email. Please try again.' });
    }
  };

  const isSending = sendEmail.isPending || sendEmailAll.isPending;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Compose Section */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Compose Email</h2>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="input"
              placeholder="Enter email subject"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Body</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="input min-h-[180px] resize-y"
              placeholder="Write your email content here..."
            />
          </div>
        </div>
      </div>

      {/* Recipients Section */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Recipients</h2>
          <div className="flex items-center bg-gray-100 rounded-xl p-1">
            <button
              onClick={() => setRecipientMode('select')}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                recipientMode === 'select'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <span className="flex items-center gap-1.5">
                <Users className="w-4 h-4" />
                Select
              </span>
            </button>
            <button
              onClick={() => setRecipientMode('all')}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                recipientMode === 'all'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              All Candidates
            </button>
          </div>
        </div>

        {recipientMode === 'select' ? (
          <div>
            <div className="relative mb-4">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="input pl-10"
                placeholder="Search candidates by name or email..."
              />
            </div>

            {selectedIds.length > 0 && (
              <p className="text-sm text-primary-600 font-medium mb-3">
                {selectedIds.length} candidate{selectedIds.length > 1 ? 's' : ''} selected
              </p>
            )}

            <div className="border border-gray-200 rounded-xl max-h-72 overflow-y-auto divide-y divide-gray-100">
              {loadingApplicants ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                </div>
              ) : applicants.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-8">No candidates found.</p>
              ) : (
                applicants.map((a) => (
                  <label
                    key={a.id}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(a.id)}
                      onChange={() => toggleSelect(a.id)}
                      className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {a.first_name} {a.last_name}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{a.email || a.phone || 'No contact'}</p>
                    </div>
                    {a.status && (
                      <span className={`badge ${getStatusColor(a.status)}`}>
                        {getStatusLabel(a.status)}
                      </span>
                    )}
                  </label>
                ))
              )}
            </div>
          </div>
        ) : (
          <div>
            <div className="flex items-center gap-3">
              <Filter className="w-4 h-4 text-gray-400" />
              <label className="text-sm font-medium text-gray-700">Filter by status (optional)</label>
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input mt-2"
            >
              <option value="">All statuses</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="responded">Responded</option>
              <option value="scheduled">Scheduled</option>
              <option value="hired">Hired</option>
              <option value="rejected">Rejected</option>
              <option value="opted_out">Opted Out</option>
            </select>
            <p className="text-xs text-gray-500 mt-2">
              Email will be sent to all candidates{statusFilter ? ` with status "${getStatusLabel(statusFilter)}"` : ''}.
            </p>
          </div>
        )}
      </div>

      {/* Feedback */}
      {feedback && (
        <div
          className={`flex items-center gap-2 p-3 rounded-xl text-sm ${
            feedback.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-700'
              : 'bg-red-50 border border-red-200 text-red-600'
          }`}
        >
          {feedback.type === 'success' ? (
            <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
          ) : (
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
          )}
          {feedback.message}
        </div>
      )}

      {/* Send */}
      <div className="flex justify-end">
        <button
          onClick={handleSend}
          disabled={isSending}
          className="btn-primary gap-2"
        >
          {isSending ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              Send Email
            </>
          )}
        </button>
      </div>
    </div>
  );
}
